import { createQuery } from "@/api/utils";
import {
    CreditBoxInvestorMap,
    CreditBoxOpportunityMap,
    CreditBoxStatus,
    CreditBoxesPaginatedResponse,
    CreditBoxMeta,
    PAGE_SIZE,
    CreditBoxResultsResponse,
    HUM_FUND_ID,
    Fund,
} from "./CreditBox.types";
import { QueryStringBuilder } from "@/utils/filtering/QueryStringBuilder";
import { camelCased } from "@/utils/misc/camelCased";

export const getCreditBoxInvestorMap = createQuery<
    CreditBoxesPaginatedResponse<CreditBoxInvestorMap>
>(({ api, params }) => {
    const queryString = new QueryStringBuilder()
        .add("size", params?.size || PAGE_SIZE)
        .add("page", params?.page || 1)
        .build();
    return api.get(`/deal-flow/v1/credit_boxes/investor_map?${queryString}`);
});

export const getCreditBoxOpportunityMap = createQuery<
    CreditBoxesPaginatedResponse<CreditBoxOpportunityMap> & {
        statuses?: string[];
    }
>(({ api, params }) => {
    const queryString = new QueryStringBuilder()
        .add("size", params?.size || PAGE_SIZE)
        .add("page", params?.page || 1)
        .build();

    return api
        .get(`/deal-flow/v1/credit_boxes/opportunity_map?${queryString}`)
        .then((res) => {
            const uniqueStatuses = [
                ...new Set(
                    res.items
                        .filter((opp) => opp.status && opp.status !== "")
                        .map((opp) => opp.status),
                ),
            ];
            return {
                ...res,
                statuses: uniqueStatuses as string[],
            };
        });
});

export const getOpportunityFieldInfo = createQuery<
    {
        label: string;
        fieldType: string;
        helpText: string;
        picklistValues: string[];
    }[]
>(({ api }) => {
    return api
        .get("/deal-flow/v1/credit_boxes/opportunity_field_info")
        .then((res) => {
            return res.items;
        });
});

export const getTaskStatus = createQuery<
    {
        fileUrl?: string;
        state: CreditBoxStatus;
        meta?: CreditBoxMeta;
    },
    { taskId: string }
>(({ api, params }) => {
    return api
        .get(`/deal-flow/v1/task_status/${params?.taskId}`)
        .then((res) => {
            return {
                ...res,
                meta: camelCased(JSON.parse(res.meta as string)),
            };
        });
});

export const getInvestmentCriteria = createQuery<
    CreditBoxResultsResponse,
    { sfdcOpportunityId: string; creditBoxIds: string[] }
>(({ api, params }) => {
    const opportunityId = (params?.sfdcOpportunityId ?? "").slice(0, 15);

    const queryBuilder = new QueryStringBuilder().add(
        "sfdc_opportunity_id",
        `eq."${opportunityId}"`,
    );

    if (params?.creditBoxIds && params.creditBoxIds.length > 0) {
        const creditBoxIds = params.creditBoxIds
            .map((id) => `"${id}"`)
            .join(",");
        queryBuilder.add("credit_box_id", `in.(${creditBoxIds})`);
    }

    const queryString = queryBuilder.build();

    return api.get(`/deal-flow/v1/credit_boxes/results/details?${queryString}`);
});

export const getFunds = createQuery<Fund[]>(({ api }) => {
    return api
        .get(`/deal-flow/v1/credit_boxes?fund_id=eq."${HUM_FUND_ID}"`)
        .then((res) => {
            return res.items;
        });
});
